<template>
  <div id="logs">
    <v-container v-if="!loadingPage" fluid>
      <v-tabs v-model="tab">
        <v-select
          flat
          :items="levels"
          dense
          auto-select-first
          v-model="selectedLevel"
          :value="levels[0]"
          solo
          item-color="white"
          @change="loadLogs('replace')"
        >
          <template v-slot:selection="data">
            <v-icon :color="data.item.color">
              {{ data.item.icon }}
            </v-icon>
          </template>

          <template slot="item" slot-scope="data">
            <v-icon :color="data.item.color"> {{ data.item.icon }}</v-icon>
          </template>
        </v-select>
        <v-tab v-for="b of this.bots" :key="b" @change="loadLogs('replace', b)">
          {{ b }}
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item v-for="(b, i) in this.bots" :key="b" :value="i">
          <div :class="`event-container bot-${b}`">
            <div v-if="showEarlierLogs && events.length">
              <v-btn block small text @click="prevLogs" :loading="loadingLogs">
                Earlier logs
              </v-btn>
            </div>
            <div v-for="(event, index) of logLines" :key="`${b}-${index}`">
              <template v-if="event.type === 'daychange'">
                <div class="daychange">
                  {{ event.previous }}
                </div>
                <v-divider />
                <div class="daychange">
                  {{ event.current }}
                </div>
              </template>
              <template v-else>
                <span
                  :class="{
                    event: true,
                    timestamp: true,
                    'l-debug': event.level === 'debug',
                    'l-info': event.level === 'info',
                    'l-warn': event.level === 'warn',
                    'l-error': event.level === 'error'
                  }"
                  @click="selectedLine = event === selectedLine ? null : event"
                >
                  {{ formatDate(event.timestamp) }}
                </span>
                <span class="message event">
                  {{ event.message }}
                </span>
              </template>
              <template v-if="event === selectedLine && event.metadata">
                <div class="metadata-table">
                  <div
                    v-for="(data, key) of event.metadata"
                    :key="`metadata-${key}`"
                    class="metadata-row"
                  >
                    <div class="metadata-cell metadata-key">{{ key }}</div>
                    <div class="metadata-cell metadata-value">
                      {{ formatMetadata(data) }}
                    </div>
                  </div>
                </div>
              </template>
            </div>
            <div>
              <v-btn block small text @click="nextLogs" :loading="loadingLogs">
                Check for More logs
              </v-btn>
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-container>
    <v-container v-if="loadingPage" fill-height fluid class="load-parent">
      <v-progress-circular
        indeterminate
        color="primary"
        class="loader"
      ></v-progress-circular>
    </v-container>
  </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import { listCloudwatch, listLoki } from "../lib/logs";
import { isLikelyUnixTimestamp } from "../lib/time";

const LEVELS = [
  {
    name: "info",
    icon: "mdi-information",
    color: "green"
  },
  {
    name: "warn",
    icon: "mdi-alert",
    color: "yellow darken-2"
  },
  {
    name: "error",
    icon: "mdi-alert-circle",
    color: "red"
  }
];

export default {
  name: "Logs",
  components: {},
  data() {
    return {
      tab: null,
      bots: [],
      loadingPage: true,
      selectedLine: null,
      events: [],
      earliestLog: null,
      latestLog: null,
      filters: {
        cloudwatch: {
          nextToken: undefined,
          prevToken: undefined,
          startTime: undefined,
          endTime: undefined
        },
        loki: {
          earliestDate: null,
          latestDate: null
        }
      },
      selectedLevel: LEVELS[0],
      levels: LEVELS,
      loadingLogs: true
    };
  },
  computed: {
    logLines() {
      const lines = this.events;

      let previousLine = null;
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (previousLine) {
          // if the two lines are on different days, insert a new line representing the date change
          if (!moment(line.timestamp).isSame(previousLine.timestamp, "day")) {
            lines.splice(i, 0, {
              type: "daychange",
              previous: moment(previousLine.timestamp).format(
                "dddd, MMMM Do YYYY"
              ),
              current: moment(line.timestamp).format("dddd, MMMM Do YYYY")
            });
          }
        }
        previousLine = line;
      }

      return lines;
    },
    showEarlierLogs() {
      return this.useCloudwatch ? !!this.filters.cloudwatch.prevToken : true;
    },
    currentBot() {
      return this.bots[this.tab];
    },
    useCloudwatch() {
      return this.selectedLevel.name === "info";
    },
    useLoki() {
      return this.selectedLevel.name !== "info";
    }
  },
  methods: {
    formatMetadata(data) {
      if (isLikelyUnixTimestamp(data)) {
        return `${data} (${moment(parseInt(data)).format(
          "DD/MM/YYYY HH:mm:ss.SSS"
        )})`;
      }

      return data;
    },
    formatDate(timestamp) {
      return moment(timestamp).format("HH:mm:ss.SS");
    },
    botNames() {
      const b = localStorage.getItem("bots") || "{}";
      return Object.keys(JSON.parse(b)).sort();
    },
    async loadLogs(action, bot) {
      this.loadingLogs = true;
      try {
        const logBot = bot || this.currentBot || this.bots[0];

        const logs = this.useCloudwatch
          ? await this.getCloudwatchLogs(logBot, action)
          : await this.getLokiLogs(logBot, action);

        const that = this;

        switch (action) {
          case "replace":
            this.events = logs.messages;

            // scroll to bottom of logs
            Vue.nextTick(() => {
              var container = that.$el.querySelector(
                `.event-container.bot-${this.currentBot}`
              );
              container.scrollTop = container.scrollHeight;
            });
            break;
          case "next":
            this.events.push(...logs.messages);
            break;
          case "previous":
            // get prev scroll height
            var prevHeight = this.$el.querySelector(
              `.event-container.bot-${this.currentBot}`
            ).scrollHeight;

            // prepend the new logs
            this.events.unshift(...logs.messages);

            Vue.nextTick(() => {
              var container = that.$el.querySelector(
                `.event-container.bot-${this.currentBot}`
              );
              // get the new scroll height
              const newHeight = container.scrollHeight;

              // scroll to the diff
              // this will keep the view at the same message as before
              container.scrollTop = newHeight - prevHeight;
            });
            break;
          default:
            this.showError(`Invalid log replacement option '${action}'`);
        }

        return logs;
      } catch (err) {
        this.showError(`Unable to read logs - ${err}`);
        return null;
      } finally {
        this.loadingLogs = false;
      }
    },
    async getLokiLogs(bot, action) {
      if (!this.filters.loki.earliestDate) {
        this.filters.loki.earliestDate = moment()
          .startOf("day")
          .valueOf();
      }

      if (!this.filters.loki.latestDate) {
        this.filters.loki.latestDate = new Date().valueOf();
      }

      let startTime = this.filters.loki.earliestDate;
      let endTime = this.filters.loki.latestDate;

      switch (action) {
        case "replace":
          // refresh and get from the start of the day until now
          this.earliestLog = null;
          this.latestLog = null;
          this.filters.loki.earliestDate = null;
          this.filters.loki.latestDate = null;

          // get from the start of the day until now
          startTime = moment()
            .startOf("day")
            .valueOf();
          endTime = new Date().valueOf();
          break;
        case "next":
          // get from the latest log until now
          startTime = this.latestLog + 1000 || new Date().valueOf();
          endTime = new Date().valueOf();
          break;
        case "previous":
          // get from 1 day earlier than earliest date, until the earliest date.

          startTime = this.filters.loki.earliestDate - 86400000;
          endTime = this.filters.loki.earliestDate;
          break;
      }

      this.filters.loki.earliestDate = Math.min(
        startTime,
        this.filters.loki.earliestDate
      );
      this.filters.loki.latestDate = Math.max(
        endTime,
        this.filters.loki.latestDate
      );

      const logs = await listLoki(
        bot,
        this.selectedLevel.name,
        startTime,
        endTime
      );

      if (logs.messages.length > 0) {
        const firstMessageTimestamp = logs.messages[0].timestamp;
        const lastMessageTimestamp =
          logs.messages[logs.messages.length - 1].timestamp;

        this.earliestLog = this.earliestLog
          ? Math.min(firstMessageTimestamp, this.earliestLog)
          : firstMessageTimestamp;
        this.latestLog = this.latestLog
          ? Math.max(lastMessageTimestamp, this.latestLog)
          : lastMessageTimestamp;
      }

      return logs;
    },
    async getCloudwatchLogs(bot, action) {
      if (!this.filters.cloudwatch.startTime || action !== "next") {
        this.filters.cloudwatch.startTime = moment()
          .startOf("day")
          .valueOf();
      }

      if (!this.filters.cloudwatch.endTime || action !== "previous") {
        this.filters.cloudwatch.endTime = new Date().valueOf();
      }

      if (action === "replace") {
        this.filters.cloudwatch.nextToken = undefined;
        this.filters.cloudwatch.prevToken = undefined;
        this.earliestLog = null;
        this.latestLog = null;
      }

      let isPrevious = false;
      let token = undefined;
      if (action === "next") {
        token = this.filters.cloudwatch.nextToken;
      } else if (action === "previous") {
        token = this.filters.cloudwatch.prevToken;
        isPrevious = true;
      }

      const logs = await listCloudwatch(
        bot,
        this.selectedLevel.name,
        isPrevious,
        token,
        this.filters.cloudwatch.startTime,
        this.filters.cloudwatch.endTime
      );

      switch (action) {
        case "replace":
          this.filters.cloudwatch.prevToken = logs.nextBackwardToken;
          this.filters.cloudwatch.nextToken = logs.nextForwardToken;
          break;
        case "next":
          this.filters.cloudwatch.nextToken = logs.nextForwardToken;
          break;
        case "previous":
          this.filters.cloudwatch.prevToken = logs.nextBackwardToken;
          break;
      }

      if (logs.messages.length > 0) {
        const firstMessageTimestamp = logs.messages[0].timestamp;
        const lastMessageTimestamp =
          logs.messages[logs.messages.length - 1].timestamp;

        this.earliestLog = this.earliestLog
          ? Math.min(firstMessageTimestamp, this.earliestLog)
          : firstMessageTimestamp;
        this.latestLog = this.latestLog
          ? Math.max(lastMessageTimestamp, this.latestLog)
          : lastMessageTimestamp;
      }

      return logs;
    },
    async prevLogs() {
      this.loadLogs("previous");
    },
    async nextLogs() {
      this.loadLogs("next");
    },
    parseEvent(event) {
      const parts = event.message.split(" - ");
      return {
        level: parts[0],
        message: parts.slice(1).join(" - "),
        timestamp: event.timestamp
      };
    },
    async refresh() {
      try {
        this.loadingPage = true;

        if (this.bots.length === 0) {
          this.bots = this.botNames();
        }
      } catch (err) {
        this.showError(`Unable to read bots - ${err}`);
      }

      const index = this.tab === null || this.tab === undefined ? 0 : this.tab;
      if (index >= 0) {
        this.loadLogs("replace");
      }

      this.loadingPage = false;
    }
  },
  async mounted() {
    this.refresh();
  }
};
</script>

<style lang="scss">
#logs {
  .load-parent {
    min-height: 300px;
    width: 100%;

    .loader {
      margin: auto;
    }
  }

  .container {
    padding-bottom: 0px;
  }

  .v-slide-group__content {
    left: 50px;
  }

  .v-select {
    width: 70px;
    position: absolute;
    top: 8px;
    left: -60px;

    div.v-list-item {
      color: white;
    }
  }

  .paging {
    text-align: center;
  }

  .v-slide-group__prev--disabled {
    display: none;
  }
}

.event-container {
  height: calc(100vh - 200px);
  overflow: auto;

  .timestamp {
    margin-right: 20px;
    font-weight: 600;
  }

  .l-debug {
    color: blue;
  }

  .l-info {
    color: green;
  }

  .l-warn {
    color: orange;
  }

  .l-error {
    color: red;
  }

  .daychange {
    font-size: 0.9em;
    font-weight: 500;
    margin-top: 12px;
    margin-bottom: 12px;
  }

  .metadata-table {
    font-size: 0.8em;
    display: grid;
    grid-template-columns: auto 1fr;
    width: 100%;
  }

  .metadata-row {
    display: contents;
  }

  .metadata-cell {
    padding: 8px;
    display: block; /* Ensures proper border and padding application */
  }

  .metadata-key {
    font-weight: 600;
  }
}

.event {
  font-family: "Courier New", Courier, monospace;
  font-size: 0.8em;
}
</style>
